"use client";

/**
 * ClassConfigForm — shared add/edit form for training class configuration.
 * Fetches quarters from quatermanagement/quater_dropdown and admins from
 * user/admin_autocomplete to populate dynamic dropdowns, matching the
 * Angular AddClassComponent behaviour.
 */

import { useRouter, useParams } from "next/navigation";
import { useEffect, useMemo, useCallback } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { useSession } from "next-auth/react";
import { useQuery } from "@tanstack/react-query";
import { useApiPost } from "@admin/_lib/api-hooks";

// ── Types ─────────────────────────────────────────────────────────────────────

interface QuarterOption { _id: string; name: string; }
interface AdminOption { _id: string; name?: string; firstname?: string; lastname?: string; }

// ── Validation schema ─────────────────────────────────────────────────────────

const classSchema = z.object({
  quater_id: z.string().min(1, "Quarter is required"),
  schedule: z.string().min(1, "Schedule is required"),
  session: z.string().min(1, "Session is required"),
  admin_id: z.string().min(1, "Assigned admin is required"),
});
type ClassValues = z.infer<typeof classSchema>;

// ── Static options ────────────────────────────────────────────────────────────

const SCHEDULE_OPTIONS = [
  { value: "schedule_a", label: "Schedule A" },
  { value: "schedule_b", label: "Schedule B" },
];

/** Session options keyed by schedule value. Sessions are constrained to the
 *  selected schedule so admins cannot choose an invalid combination. */
const SESSION_OPTIONS_BY_SCHEDULE: Record<string, { value: string; label: string }[]> = {
  schedule_a: [
    { value: "session_1a", label: "Session 1A — Schedule A, Slot 1" },
    { value: "session_1b", label: "Session 1B — Schedule A, Slot 2" },
    { value: "session_1c", label: "Session 1C — Schedule A, Slot 3" },
  ],
  schedule_b: [
    { value: "session_2a", label: "Session 2A — Schedule B, Slot 1" },
    { value: "session_2b", label: "Session 2B — Schedule B, Slot 2" },
    { value: "session_2c", label: "Session 2C — Schedule B, Slot 3" },
  ],
};

// ── Helper ────────────────────────────────────────────────────────────────────

function getBaseUrl() {
  return process.env.NEXT_PUBLIC_API_CLOUDFRONT ?? "https://api.divineinfinitebeing.com/";
}

function adminDisplayName(a: AdminOption) {
  return a.name ?? ([a.firstname, a.lastname].filter(Boolean).join(" ") || a._id);
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ClassConfigForm({ mode }: { mode: "add" | "edit" }) {
  const router = useRouter();
  const params = useParams();
  const classId = mode === "edit" ? (params?.id as string) : undefined;
  const { data: session } = useSession();

  // ── Dynamic dropdown data ─────────────────────────────────────────────────

  const { data: quarters, isLoading: qLoading } = useQuery<QuarterOption[]>({
    queryKey: ["quarter-dropdown"],
    queryFn: async () => {
      const res = await fetch(`${getBaseUrl()}quatermanagement/quater_dropdown`, {
        headers: { Authorization: `Bearer ${session?.token ?? ""}` },
      });
      const data = await res.json();
      const result = data.results ?? data.data ?? [];
      return (Array.isArray(result) ? result : []) as QuarterOption[];
    },
    enabled: !!session?.token,
    staleTime: 5 * 60 * 1000,
  });

  const { data: admins, isLoading: aLoading } = useQuery<AdminOption[]>({
    queryKey: ["admin-autocomplete"],
    queryFn: async () => {
      const res = await fetch(`${getBaseUrl()}user/admin_autocomplete`, {
        headers: { Authorization: `Bearer ${session?.token ?? ""}` },
      });
      const data = await res.json();
      const result = data.results ?? data.data ?? [];
      return (Array.isArray(result) ? result : []) as AdminOption[];
    },
    enabled: !!session?.token,
    staleTime: 5 * 60 * 1000,
  });

  // ── Existing record (edit mode) ───────────────────────────────────────────

  const { data: existing, isLoading: fetchLoading } = useQuery<ClassValues & { _id?: string }>({
    queryKey: ["class-config", classId],
    queryFn: async () => {
      const res = await fetch(`${getBaseUrl()}quatermanagement/edit_class_data`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.token ?? ""}`,
        },
        body: JSON.stringify({ id: classId }),
      });
      const data = await res.json();
      return data.results ?? data.data ?? {};
    },
    enabled: mode === "edit" && !!classId && !!session?.token,
  });

  // ── Form ──────────────────────────────────────────────────────────────────

  const form = useForm<ClassValues>({
    resolver: zodResolver(classSchema),
    defaultValues: { quater_id: "", schedule: "", session: "", admin_id: "" },
  });

  const { reset, setValue } = form;

  // CC-02: Watch schedule to derive available session options
  const selectedSchedule = useWatch({ control: form.control, name: "schedule" });
  const sessionOptions = useMemo(
    () => SESSION_OPTIONS_BY_SCHEDULE[selectedSchedule] ?? [],
    [selectedSchedule]
  );

  // CC-02: Clear session when schedule changes and current session is invalid
  const currentSession = useWatch({ control: form.control, name: "session" });
  const clearInvalidSession = useCallback(() => {
    const valid = SESSION_OPTIONS_BY_SCHEDULE[selectedSchedule]?.some(
      (o) => o.value === currentSession
    );
    if (!valid && currentSession) {
      setValue("session", "", { shouldValidate: false });
    }
  }, [selectedSchedule, currentSession, setValue]);

  useEffect(() => {
    clearInvalidSession();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchedule]);

  // CC-03: Edit hydration — reset with saved schedule first so session options
  // are derived correctly before the saved session value is applied
  useEffect(() => {
    if (existing) {
      reset({
        quater_id: existing.quater_id ?? "",
        schedule: existing.schedule ?? "",
        session: existing.session ?? "",
        admin_id: existing.admin_id ?? "",
      });
    }
  }, [existing, reset]);

  // ── Mutations ─────────────────────────────────────────────────────────────

  const addMutation = useApiPost("quatermanagement/add_class_config", { invalidateKeys: ["quatermanagement/class_list", "quatermanagement/class_list-count"] });
  const editMutation = useApiPost("quatermanagement/edit_class_config", { invalidateKeys: ["quatermanagement/class_list", "quatermanagement/class_list-count"] });

  const isPending = addMutation.isPending || editMutation.isPending;

  async function onSubmit(values: ClassValues) {
    try {
      if (mode === "edit" && classId) {
        await editMutation.mutateAsync({ ...values, id: classId });
        toast.success("Class updated");
      } else {
        await addMutation.mutateAsync(values);
        toast.success("Class created");
      }
      router.push("/admin/training/classes");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to save class");
    }
  }

  // ── Skeleton while fetching existing record ───────────────────────────────

  const isPageLoading = mode === "edit" && fetchLoading;

  const quarterOptions = useMemo(
    () => (Array.isArray(quarters) ? quarters : []).map((q) => ({ value: q._id, label: q.name })),
    [quarters]
  );
  const adminOptions = useMemo(
    () => (Array.isArray(admins) ? admins : []).map((a) => ({ value: a._id, label: adminDisplayName(a) })),
    [admins]
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="h-7 w-1 rounded-full shrink-0" style={{ background: "linear-gradient(to bottom, #ffa800, #ff7e00)" }} />
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-foreground">
            {mode === "edit" ? "Edit Class Configuration" : "Add Class Configuration"}
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Configure a training class for a specific quarter and schedule
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={() => router.back()}>
          <ArrowLeft className="size-4" />
        </Button>
      </div>

      {isPageLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-full rounded-lg" />
          <Skeleton className="h-10 w-full rounded-lg" />
          <Skeleton className="h-10 w-full rounded-lg" />
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Class Details</CardTitle>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Quarter — dynamic dropdown from API */}
                  <FormField
                    control={form.control}
                    name="quater_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Quarter</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value} disabled={qLoading}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder={qLoading ? "Loading quarters…" : "Select a quarter"} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {quarterOptions.map((o) => (
                              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Schedule */}
                  <FormField
                    control={form.control}
                    name="schedule"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Schedule</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select schedule" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {SCHEDULE_OPTIONS.map((o) => (
                              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  {/* Session — options derived from selected schedule (CC-02) */}
                  <FormField
                    control={form.control}
                    name="session"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Session</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                          disabled={!selectedSchedule}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue
                                placeholder={
                                  selectedSchedule
                                    ? "Select session"
                                    : "Select a schedule first"
                                }
                              />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {sessionOptions.map((o) => (
                              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Assigned Admin — dynamic dropdown from API */}
                  <FormField
                    control={form.control}
                    name="admin_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Assigned Admin</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value} disabled={aLoading}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder={aLoading ? "Loading admins…" : "Select an admin"} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {adminOptions.map((o) => (
                              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="submit" disabled={isPending}>
                    {isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
                    {mode === "edit" ? "Update Class" : "Create Class"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => router.back()}>
                    Cancel
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
